import React from 'react';
import {observer} from 'mobx-react';
import {PerformanceReviewCycle} from 'stores/performance_reviews';
import DirectoryCycleName from './DirectoryCycleName';
import {CycleDirectoryState} from '../state';
import {Layout, InteractiveTable, EmptyState, HelperPanel, DropdownFilter} from 'components';
import {t, withState} from 'shared/core';
import {auth, types, endpoints} from 'main/tools';
import {withRouter} from 'react-router-dom';
import PerformanceReviewEmptyState from 'img/performance-review-empty-state@2x.png';
import useModal from 'shared/hooks/useModal';
import ReopenCycleModal from './ReopenCycleModal';

const COLUMNS = [
  {
    header: 'performance_reviews.directory.Review name',
    width: 11,
    component: DirectoryCycleName
  }
];

const LOCALIZATION = {
  emptyState: 'performance_reviews.directory.No performance reviews have been created yet',
  removeModal: {
    header: 'performance_reviews.directory.Remove performance review?',
    subHeader: 'performance_reviews.directory.Are you sure you want to remove this performance review?',
    body: 'performance_reviews.directory.This performance review, including any feedback already submitted, will be permanently deleted.'
  },
  interactive: {
    addModel: 'performance_reviews.directory.Create New Review'
  }
};

const LINKS = [
  {
    articleId: '1967931',
    title: 'performance_reviews.What is a Performance Review?'
  },
  {
    articleId: '1967932',
    title: 'performance_reviews.What are Question Sets?'
  },
  {
    articleId: '1976267',
    title: 'performance_reviews.Launching a Performance Review'
  },
  {
    articleId: '1967933',
    title: 'performance_reviews.Reviews vs. Check-Ins'
  },
  {
    articleId: '1976341',
    title: 'performance_reviews.Closing and Sharing Performance Reviews'
  }
];

const statusFilters = () => {
  return [
    {
      id: 'active',
      display: t('performance_reviews.directory.Active')
    },
    {
      id: 'ended',
      display: t('performance_reviews.directory.Closed')
    }
  ];
};

const Empty = observer(({uiState}) => {
  return (
    <EmptyState
      img={PerformanceReviewEmptyState}
      title={t('performance_reviews.directory.empty_state.TITLE')}
      descriptionItems={[t('performance_reviews.directory.empty_state.DESCRIPTION')]}
      callToAction={{
        onClick: () => uiState.addNewCycle(),
        title: t('performance_reviews.directory.empty_state.Start a Review')
      }}
    />
  );
});

const ReviewCycleDirectory = observer(({uiState, managesPerformance}) => {
  const reopenCycleModalOpen = useModal(uiState, 'ReopenCycleModal', 'selectedCycle');

  return (
    <>
      <InteractiveTable
        uiState={uiState}
        title={t('performance_reviews.Performance review cycles')}
        columns={COLUMNS}
        saveStateToUrl={true}
        searchable={true}
        localization={LOCALIZATION}
        onAdd={managesPerformance ? () => uiState.addNewCycle() : null}
        onRemove={cycle => uiState.removeCycle(cycle)}
        editRoute='/reviews/:id/edit'
        empty={managesPerformance ? Empty : null}
        onMount={agent => uiState.setInteractiveAgent(agent)}
        initialFilter={{
          status: statusFilters()[0].id
        }}
        customLinks={cycle => uiState.customLinksFor(cycle)}
        proxy={{
          type: 'async',
          modelType: types.PERFORMANCE_REVIEWS.CYCLE,
          model: PerformanceReviewCycle,
          endpoint: endpoints.PERFORMANCE_REVIEWS.CYCLES.DIRECTORY
        }}
      >
        <DropdownFilter options={statusFilters()} attr='status'/>
      </InteractiveTable>
      <ReopenCycleModal uiState={uiState} modalOpen={reopenCycleModalOpen} />
    </>
  );
});

const ReviewCycleList = observer(({uiState}) => {
  const managesPerformance = auth.hasAccess('::MANAGE_PERFORMANCE_MANAGEMENT');

  if (managesPerformance) {
    return (
      <Layout>
        <Layout.Section>
          <ReviewCycleDirectory uiState={uiState} managesPerformance={managesPerformance}/>
        </Layout.Section>
        <Layout.Section secondary>
          <HelperPanel
            links={LINKS}
            collectionId={228100}
          />
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <Layout>
      <ReviewCycleDirectory uiState={uiState} managesPerformance={managesPerformance}/>
    </Layout>
  );
});

export default withRouter(withState(ReviewCycleList, CycleDirectoryState));
